/**
 * CLE-2811 (2026-09-28) — Ani: "In AI modeling I also need to see my notes."
 *
 * A run tied to a contract shows, on its Contract Terms step, what that contract holds right now
 * (`IDraftRunDetail.contractOnFile`, read live on every fetch): her notes first, then — folded under
 * "Saved on this contract" — the facts already saved on it. The contract term's "In production now" block
 * reads the same rows. Pure, so the rows and the schedule's line cap are tested without a DOM.
 *
 * The server caps each reimbursement slot at 20,000 characters (`truncatedSlots` names the ones it cut):
 * such a schedule's "Show all" expands what was received and ends by pointing to the contract.
 */

import {
  MULTIPLE_PROCEDURE_METHODOLOGY,
  REIMBURSEMENT_SLOTS_BY_CONTRACT_TYPE,
  ReimbursementSlot,
  reimbursementSlotLabel,
} from "pages/PayerRules/contracts/helpers";

import { IContractOnFile } from "../../../store/queries/contractModelDrafts";

export const YOUR_NOTES_ON_CONTRACT = "Your notes on this contract";
export const NO_NOTES_ON_CONTRACT = "No notes on this contract";
export const SAVED_ON_CONTRACT = "Saved on this contract";
export const NOT_RECORDED = "Not recorded";
export const OPEN_CONTRACT = "Open contract";
/** After a schedule the server cut short: followed by an "Open contract" link. */
export const FULL_SCHEDULE_ON_CONTRACT = "The full schedule is on the contract";

/** A reimbursement schedule is often a row per code: the first lines, then "Show all". */
export const SCHEDULE_PREVIEW_LINES = 10;

export interface IOnFileRow {
  key: string;
  label: string;
  /** Null when the contract has nothing recorded ("Not recorded"). */
  value: string | null;
  /** A reimbursement schedule: shown with its line breaks, capped at `SCHEDULE_PREVIEW_LINES`. */
  schedule?: boolean;
  /** A schedule the server cut at its cap (`truncatedSlots`): `value` is only its beginning. */
  truncated?: boolean;
}

const textOf = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v : null;

const ALL_SLOTS: ReimbursementSlot[] = [
  "inpatient",
  "outpatient",
  "facility",
  "nonFacility",
];

/**
 * The saved values, in the Contract Terms form's order: type, signed date, fee effective date, timely
 * filing, implants, the MPPR methodology, then the reimbursement schedule in the shape the contract type
 * calls for, labelled as everywhere else (`reimbursementSlotLabel`). With no type, any slot that holds
 * something is still shown — a schedule saved before the type must not vanish. A slot named in
 * `truncatedSlots` (and holding text) is marked `truncated`.
 */
export const contractOnFileRows = (c: IContractOnFile): IOnFileRow[] => {
  const type = c.contractType ?? null;
  const typeSlots = type ? REIMBURSEMENT_SLOTS_BY_CONTRACT_TYPE[type] : null;
  const slots =
    typeSlots ?? ALL_SLOTS.filter((s) => textOf(c.reimbursement?.[s]));
  const cut = new Set(Array.isArray(c.truncatedSlots) ? c.truncatedSlots : []);
  return [
    { key: "contractType", label: "Contract type", value: type },
    { key: "signedDate", label: "Signed date", value: textOf(c.signedDate) },
    {
      key: "effectiveDate",
      label: "Effective date",
      value: textOf(c.effectiveDate),
    },
    {
      key: "timelyFilingDays",
      label: "Timely filing",
      value:
        typeof c.timelyFilingDays === "number"
          ? `${c.timelyFilingDays} days from DOS`
          : null,
    },
    {
      key: "implantTerms",
      label: "Implant terms",
      value: textOf(c.implantTerms),
    },
    {
      key: "multipleProcedureMethodology",
      label: MULTIPLE_PROCEDURE_METHODOLOGY,
      value: textOf(c.multipleProcedureMethodology),
    },
    ...slots.map((slot) => {
      const value = textOf(c.reimbursement?.[slot]);
      return {
        key: `reimbursement.${slot}`,
        label: reimbursementSlotLabel(slot, type),
        value,
        schedule: true,
        ...(value !== null && cut.has(slot) ? { truncated: true } : {}),
      };
    }),
  ];
};

/** The first `limit` lines of a schedule, and how many more there are. */
export const schedulePreview = (
  text: string,
  limit = SCHEDULE_PREVIEW_LINES
): { shown: string; hiddenLines: number } => {
  const lines = text.split(/\r?\n/);
  return {
    shown: lines.slice(0, limit).join("\n"),
    hiddenLines: Math.max(0, lines.length - limit),
  };
};

/** Her notes, or null when there are none ("No notes on this contract"). Line breaks are kept. */
export const contractNotes = (c: IContractOnFile): string | null =>
  textOf(c.notes);

/**
 * Whether the contract on file IS this term's production: the term is the contract's own payer's (or
 * names none). An off-payer term (SuperMed on the Medflex contract) is compared the usual way instead.
 */
export const onFileIsTermsProduction = (
  c: IContractOnFile | null | undefined,
  termPayerId: number | null
): c is IContractOnFile =>
  !!c && (termPayerId == null || termPayerId === c.payerId);
